/**
 * Anlagenverzeichnis: die buchhalterische Sicht auf die Risikoobjekte.
 *
 * Ein Anlageverzeichnis kommt aus der Finanzbuchhaltung und trägt je Position
 * Konto, Anschaffungsdatum und AHK (Anschaffungs- und Herstellungskosten). Für
 * die Versicherung zählt davon keine einzige Zahl direkt: versichert wird der
 * **Neuwert**, also die AHK hochgerechnet mit dem Index der zugehörigen Sparte.
 * Beide Werte gehören nebeneinander — die AHK ist der Beleg, der Neuwert die
 * Versicherungssumme.
 *
 * Die Merkmale sind optional. Ein Kontakt ohne Anlageverzeichnis behält die
 * Ansicht, die er vorher hatte: die Spalten erscheinen nur, wenn Daten da sind.
 */

export interface CharLike {
  nameBezeichnung?: string | null;
  riskCharacteristicValue?: string | null;
}

/** Merkmalsschlüssel je Feld. Erster Treffer gewinnt. */
export const AV_KEYS = {
  konto: ["Konto", "Kontonummer", "Konto Nr."],
  kontoBezeichnung: ["Kontobezeichnung"],
  anschaffungsdatum: ["Anschaffungsdatum"],
  anschaffungsjahr: ["Anschaffungsjahr"],
  ahk: ["AHK", "Anschaffungswert"],
  indexsparte: ["Indexsparte"],
  indexierung: ["Neuwertindexierung"],
} as const;

const charWert = (chars: CharLike[] | null | undefined, keys: readonly string[]): string | null => {
  for (const key of keys) {
    const hit = chars?.find(
      (c) => (c.nameBezeichnung ?? "").trim().toLowerCase() === key.toLowerCase(),
    );
    const val = hit?.riskCharacteristicValue?.trim();
    if (val) return val;
  }
  return null;
};

/**
 * Betrag aus einem Merkmal. Merkmalswerte sind Text — je nachdem, ob sie aus
 * einem Import oder aus der Tastatur kommen, steht dort „1234.56" oder
 * „1.234,56".
 *
 * Der mehrdeutige Fall ist ein einzelner Punkt mit genau drei Ziffern dahinter:
 * „1.234" ist auf deutsch Eintausendzweihundertvierunddreißig, in
 * Maschinenschreibweise 1,234. Wir lesen ihn als Tausendertrenner — Beträge in
 * Euro haben zwei Nachkommastellen, nicht drei.
 */
export const betragAus = (roh: string | null | undefined): number | null => {
  const s = String(roh ?? "").replace(/[\s€]/g, "");
  if (!s) return null;
  let norm: string;
  if (s.includes(",")) {
    norm = s.replace(/\./g, "").replace(",", ".");
  } else if ((s.match(/\./g)?.length ?? 0) > 1) {
    norm = s.replace(/\./g, "");
  } else if (/^-?\d+\.\d{3}$/.test(s)) {
    norm = s.replace(".", "");
  } else {
    norm = s;
  }
  const n = Number(norm);
  return Number.isFinite(n) ? n : null;
};

/** Jahr aus „2016", „2016-05-01" oder „01.05.2016". */
export const jahrAus = (roh: string | null | undefined): number | null => {
  const s = String(roh ?? "").trim();
  if (!s) return null;
  const iso = /^(\d{4})-\d{2}-\d{2}/.exec(s);
  if (iso) return Number(iso[1]);
  const de = /(\d{4})\s*$/.exec(s);
  if (de) return Number(de[1]);
  return null;
};

export interface AnlagePosition {
  /** Sachkonto der Buchhaltung, z. B. „400". */
  konto: string | null;
  kontoBezeichnung: string | null;
  /** Anschaffungsjahr — die Basis jeder Indexrechnung. */
  jahr: number | null;
  /** Anschaffungs- und Herstellungskosten in Euro. */
  ahk: number | null;
  /** Index-Sparte, mit der der Neuwert gerechnet wurde. */
  indexsparte: string | null;
  /**
   * Ob für diese Position überhaupt ein Neuwert gerechnet wird. Kfz, geringwertige
   * Wirtschaftsgüter und Anzahlungen sind bewusst ausgenommen: für Kfz gibt es
   * keinen Index, GWG sind im Zugangsjahr voll abgeschrieben, Anzahlungen sind
   * noch nicht in Betrieb. Eine 0 € dort wäre eine Aussage, ein „n/a" ist die
   * Wahrheit.
   */
  indexiert: boolean;
  /** Grund der Ausnahme, falls angegeben. */
  indexierungHinweis: string | null;
}

const AUSGESCHLOSSEN = /^(ausgeschlossen|nein|n\/a|keine)/i;

export const anlageDatenAus = (chars: CharLike[] | null | undefined): AnlagePosition => {
  const indexRoh = charWert(chars, AV_KEYS.indexierung);
  return {
    konto: charWert(chars, AV_KEYS.konto),
    kontoBezeichnung: charWert(chars, AV_KEYS.kontoBezeichnung),
    jahr:
      jahrAus(charWert(chars, AV_KEYS.anschaffungsjahr)) ??
      jahrAus(charWert(chars, AV_KEYS.anschaffungsdatum)),
    ahk: betragAus(charWert(chars, AV_KEYS.ahk)),
    indexsparte: charWert(chars, AV_KEYS.indexsparte),
    indexiert: !(indexRoh && AUSGESCHLOSSEN.test(indexRoh)),
    indexierungHinweis: indexRoh && AUSGESCHLOSSEN.test(indexRoh) ? indexRoh : null,
  };
};

/** Trägt die Position überhaupt Buchhaltungsdaten? */
export const hatAnlageDaten = (a: AnlagePosition): boolean =>
  a.konto != null || a.jahr != null || a.ahk != null;

/**
 * Sortierung des Verzeichnisses: nach Konto wie in der Buchhaltung, innerhalb
 * des Kontos nach der Positionsnummer. Positionen ohne Konto hängen hinten an,
 * sonst stünde eine handgepflegte Zeile mitten in einem importierten Konto.
 */
export const anlageSort = (
  a: { anlage: AnlagePosition; nr: string },
  b: { anlage: AnlagePosition; nr: string },
): number => {
  const ka = a.anlage.konto;
  const kb = b.anlage.konto;
  if (ka == null && kb != null) return 1;
  if (ka != null && kb == null) return -1;
  if (ka != null && kb != null && ka !== kb) {
    const na = Number(ka);
    const nb = Number(kb);
    if (Number.isFinite(na) && Number.isFinite(nb)) return na - nb;
    return ka.localeCompare(kb, "de");
  }
  return a.nr.localeCompare(b.nr, "de", { numeric: true });
};

/** Eine Position, so weit die Konten-Zusammenfassung sie braucht. */
export interface AnlageZeile {
  anlage: AnlagePosition;
  /** Versicherungssumme / Neuwert der Position. */
  wert: number;
  nr: string;
}

export interface KontoGruppe<T extends AnlageZeile> {
  /** Sortier- und React-Schlüssel; „—" für Positionen ohne Konto. */
  key: string;
  konto: string | null;
  bezeichnung: string | null;
  list: T[];
  ahk: number;
  /** Neuwert-Summe OHNE die ausgenommenen Positionen. */
  neuwert: number;
  /** Wie viele der Positionen einen Neuwert tragen. */
  indexiert: number;
}

export interface KontenUebersicht<T extends AnlageZeile> {
  gruppen: KontoGruppe<T>[];
  positionen: number;
  ahkGesamt: number;
  neuwertGesamt: number;
  /** Positionen ohne Neuwert (Kfz, GWG, Anzahlungen). */
  ausgenommen: T[];
  ahkAusgenommen: number;
  /** Anteil der ausgenommenen AHK am Buchbestand, in Prozent. */
  anteilAusgenommen: number;
}

/** AHK-Summe; Positionen ohne AHK zählen als 0. */
const ahkSumme = (list: AnlageZeile[]): number =>
  list.reduce((acc, r) => acc + (r.anlage.ahk ?? 0), 0);

/**
 * Neuwert-Summe OHNE die ausgenommenen Positionen.
 *
 * Ein „n/a" darf nicht als 0 mitgerechnet werden: Die Summe sähe dann
 * vollständig aus, obwohl für ein Viertel des Bestands nie ein Neuwert
 * gerechnet wurde.
 */
const neuwertSumme = (list: AnlageZeile[]): number =>
  list.filter((r) => r.anlage.indexiert).reduce((acc, r) => acc + r.wert, 0);

/**
 * Zusammenfassung je Sachkonto — das Gegenstück zur Neuwertberechnung des
 * Kunden, Blatt „Zusammenfassung je Konto".
 *
 * Rechnet über ALLE übergebenen Positionen, auch über Kfz und Bauten auf
 * fremdem Grundstück. Die Vermögensmatrix zeigt die nicht (sie haben eigene
 * Verträge und eigene Datenquellen) — für den Abgleich mit der Buchhaltung
 * müssen sie aber dabei sein, sonst fehlt in der Summe genau das Konto, dessen
 * Fehlen niemandem auffällt.
 */
export function kontenZusammenfassung<T extends AnlageZeile>(rows: T[]): KontenUebersicht<T> {
  const map = new Map<string, KontoGruppe<T>>();
  for (const r of rows) {
    const key = r.anlage.konto ?? "—";
    const vorhanden = map.get(key);
    if (vorhanden) vorhanden.list.push(r);
    else
      map.set(key, {
        key,
        konto: r.anlage.konto,
        // Die Bezeichnung der ERSTEN Position gilt fürs ganze Konto: sie steht
        // in jeder Zeile des Verzeichnisses und ist dort dieselbe.
        bezeichnung: r.anlage.kontoBezeichnung,
        list: [r],
        ahk: 0,
        neuwert: 0,
        indexiert: 0,
      });
  }

  const gruppen = Array.from(map.values())
    .map((g) => {
      const list = [...g.list].sort(anlageSort);
      return {
        ...g,
        list,
        ahk: ahkSumme(list),
        neuwert: neuwertSumme(list),
        indexiert: list.filter((r) => r.anlage.indexiert).length,
      };
    })
    .sort((a, b) => anlageSort(a.list[0], b.list[0]));

  const ausgenommen = rows.filter((r) => !r.anlage.indexiert);
  const ahkGesamt = ahkSumme(rows);
  const ahkAusgenommen = ahkSumme(ausgenommen);

  return {
    gruppen,
    positionen: rows.length,
    ahkGesamt,
    neuwertGesamt: neuwertSumme(rows),
    ausgenommen,
    ahkAusgenommen,
    anteilAusgenommen: ahkGesamt > 0 ? (ahkAusgenommen / ahkGesamt) * 100 : 0,
  };
}

/** Beschriftung der Indexierungs-Spalte eines Kontos. */
export const kontoIndexStatus = (g: KontoGruppe<AnlageZeile>): string => {
  if (g.indexiert === 0) return "ausgenommen";
  if (g.indexiert === g.list.length) return "indexiert";
  return `${g.indexiert} von ${g.list.length} indexiert`;
};
